import { Component } from "react";
import { connect } from "react-redux";

import { ActionButton, ActionButtonIntent } from "widgets";

import DataTableContainer from "../../core/grid/DataTableContainer.js";
import { ConfirmationDialog } from "../../core/overlays/Dialogs.js";
import { ModalActionWidget } from "../../core/overlays/index.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import { type InputTableQuerySpec } from "@/graphql.ts";
import { downloadGenomeList, selectGenomeAssemblies } from "@/redux/modules/ncbi-admin.ts";
import {
  type ITableData,
  createUniqueKeyFromTableSpec,
  getTableDataSlice,
  selectTableRows,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";

type INCBIAgentSelectGenomesScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface INCBIAgentSelectGenomesScreenAppStateProps {
  isLoadingData: boolean;
  downloadingGenomeList: boolean;
  selectedIDs?: number[];
  preselectedIds?: number[];
}

interface INCBIAgentSelectGenomesScreenDispatchProps {
  selectGenomeAssemblies: (selectedGenomeIds: number[]) => void;
  downloadGenomeList: () => void;
  selectTableRows: (tableSpec: InputTableQuerySpec, ids: number[]) => void;
}

export interface INCBIAgentSelectGenomesScreenProps
  extends INCBIAgentSelectGenomesScreenAppStateProps,
    INCBIAgentSelectGenomesScreenDispatchProps,
    INCBIAgentSelectGenomesScreenOwnProps,
    IPlaybookWizardStepInjectedProps {}

interface INCBIAgentSelectGenomesScreenState {
  showValidationMessage: boolean;
}

export const ncbiConfigureTableSpec: InputTableQuerySpec = {
  resultType: ENTITY_TYPE.GENOMEASSEMBLY,
};

function mapStateToProps(
  state: IAppState,
  _ownProps: INCBIAgentSelectGenomesScreenOwnProps,
): INCBIAgentSelectGenomesScreenAppStateProps {
  const tableData: ITableData = getTableDataSlice(state.tableData, ncbiConfigureTableSpec);
  let ids: number[] = [];
  if (tableData) {
    ids = tableData.selectionModel.selectedIds as number[];
  }
  return {
    isLoadingData: state.ncbiAgent.loading,
    downloadingGenomeList: state.ncbiAgent.downloadingGenomeAssemblyList,
    selectedIDs: ids,
    preselectedIds: state.ncbiAgent.selectedGenomeIds,
  };
}

function mapDispatchToProps(dispatch): INCBIAgentSelectGenomesScreenDispatchProps {
  return {
    selectGenomeAssemblies: (selectedGenomeIds: number[]) => dispatch(selectGenomeAssemblies(selectedGenomeIds)),
    downloadGenomeList: () => dispatch(downloadGenomeList(true)),
    selectTableRows: (tableSpec: InputTableQuerySpec, ids: number[]) =>
      dispatch(selectTableRows(tableSpec, ids, true, true)),
  };
}

/**
 * First screen of the sample registration playbook (configuration of the playbook).
 */
class NCBIAgentSelectGenomesScreen extends Component<
  INCBIAgentSelectGenomesScreenProps,
  INCBIAgentSelectGenomesScreenState
> {
  constructor(props) {
    super(props);
    this.state = {
      showValidationMessage: false,
    };
  }

  componentDidUpdate(prevProps: Readonly<INCBIAgentSelectGenomesScreenProps>) {
    if (this.props.selectedIDs !== prevProps.selectedIDs) {
      this.setState({ showValidationMessage: false });
    }
  }

  applyPreviousSelection = () => {
    // restore assembly selection if we came here via "Go back"
    if (this.props.preselectedIds) {
      this.props.selectTableRows(ncbiConfigureTableSpec, this.props.preselectedIds);
    }
  };

  render() {
    return (
      <PlaybookWizardStep
        {...this.props}
        wizardFooterData={{
          nextButtonProps: {
            label: "Next",
            isDisabled: this.props.downloadingGenomeList,
            onPress: this.handleSubmitData,
          },
          error: this.state.showValidationMessage && "Select one or more genomes for import",
        }}
      >
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
          <div className="admin-tools-item-title">
            <p>
              Available For Download
              <span style={{ float: "right" }}>
                Update to Newest NCBI Genome List
                <ModalActionWidget
                  actionWidget={({ open }) => (
                    <ActionButton
                      style={{ marginLeft: 20 }}
                      icon={"sync"}
                      label={"Refresh"}
                      isLoading={this.props.downloadingGenomeList}
                      className={"refresh-icon"}
                      onPress={() => open()}
                    />
                  )}
                >
                  {({ close }) => (
                    <ConfirmationDialog
                      headerProps={{
                        title: "Download NCBI Genome List",
                      }}
                      mainActionButtonProps={{
                        children: "Download",
                        onPress: () => {
                          // we do not wait with closing the confirmation dialog until the download finished,
                          // but the running download is indicated by a spinning download button
                          this.props.downloadGenomeList();
                          close();
                        },
                        intent: ActionButtonIntent.Danger,
                      }}
                      closeFn={close}
                    >
                      <div>Are you sure that you want to download the latest genome list from NCBI?</div>
                      <div>This operation may take hours to complete depending on the connection speed.</div>
                      <div>The download will continue in the background if you navigate away from this page.</div>
                    </ConfirmationDialog>
                  )}
                </ModalActionWidget>
              </span>
            </p>
          </div>
          <div className={"ncbi-assembly-table"}>
            <DataTableContainer
              key={createUniqueKeyFromTableSpec(ncbiConfigureTableSpec)}
              tableSpec={ncbiConfigureTableSpec}
              tableInitializationCallback={this.applyPreviousSelection}
            />
          </div>
        </div>
      </PlaybookWizardStep>
    );
  }

  handleSubmitData = () => {
    if (this.props.selectedIDs != null && this.props.selectedIDs.length > 0) {
      this.props.selectGenomeAssemblies(this.props.selectedIDs);
      this.props.proceedWithNextStep();
    } else {
      this.setState({ showValidationMessage: true });
    }
  };
}

export default connect<
  INCBIAgentSelectGenomesScreenAppStateProps,
  INCBIAgentSelectGenomesScreenDispatchProps,
  INCBIAgentSelectGenomesScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(NCBIAgentSelectGenomesScreen as any);
export { NCBIAgentSelectGenomesScreen };
